import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useShop } from "@/contexts/ShopContext";
import { useLang } from "@/contexts/LanguageContext";
import { api, API_BASE } from "@/lib/api";
import { REP } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Mail, TrendingUp, TrendingDown, Send, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

function fmtEur(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function Reports() {
  const { selectedShopId, selectedShop } = useShop();
  const { t } = useLang();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");

  // subscription local state
  const [subEmail, setSubEmail] = useState("");
  const [subEnabled, setSubEnabled] = useState(false);
  const [subWeekday, setSubWeekday] = useState("mon");
  const [subHour, setSubHour] = useState("8");

  const { data: digest } = useQuery({
    queryKey: ["digest", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/digest`)).data,
    enabled: !!selectedShopId,
  });

  const { data: integrations } = useQuery({
    queryKey: ["integrations"],
    queryFn: async () => (await api.get(`/integrations/status`)).data,
  });

  const { data: subscription } = useQuery({
    queryKey: ["subscription", selectedShopId],
    queryFn: async () => (await api.get(`/shops/${selectedShopId}/digest/subscription`)).data,
    enabled: !!selectedShopId,
  });

  useEffect(() => {
    if (subscription) {
      setSubEmail(subscription.email || "");
      setSubEnabled(!!subscription.enabled);
      setSubWeekday(subscription.weekday || "mon");
      setSubHour(String(subscription.hour ?? 8));
    }
  }, [subscription]);

  const emailMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/digest/email`, { recipient_email: email })).data,
    onSuccess: (data) => {
      if (data.sent) {
        toast.success(t("email_sent"));
        setEmail("");
      } else {
        toast.error(data.reason || t("email_configure_hint"));
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const saveSubMutation = useMutation({
    mutationFn: async () =>
      (await api.put(`/shops/${selectedShopId}/digest/subscription`, {
        email: subEmail,
        enabled: subEnabled,
        weekday: subWeekday,
        hour: parseInt(subHour, 10),
      })).data,
    onSuccess: () => {
      toast.success(t("saved"));
      qc.invalidateQueries({ queryKey: ["subscription", selectedShopId] });
    },
    onError: () => toast.error("Save failed"),
  });

  const runNowMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/shops/${selectedShopId}/digest/subscription/run-now`)).data,
    onSuccess: (data) => {
      if (data.sent) toast.success(t("email_sent"));
      else toast.error(data.reason || "Send failed");
      qc.invalidateQueries({ queryKey: ["subscription", selectedShopId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/shops/${selectedShopId}/digest/pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rankly-${selectedShop?.domain}-${digest?.week_of}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF download failed");
    }
  };

  const revChange = digest?.revenue_change_pct ?? 0;
  const visChange = digest?.visibility_change_pct ?? 0;
  const emailEnabled = integrations?.resend;

  return (
    <div data-testid={REP.root} className="space-y-6 rk-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
            {selectedShop?.domain} · week of {digest?.week_of ?? "—"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {t("reports_title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{t("reports_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={REP.downloadPdfBtn}
            onClick={handleDownloadPdf}
            variant="outline"
            className="h-10 gap-2"
          >
            <Download className="h-4 w-4" />
            {t("download_pdf")}
          </Button>
        </div>
      </header>

      {/* KPI band */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BigKpi label={t("revenue_this_week")} value={fmtEur(digest?.revenue_estimate_eur)} delta={revChange} />
        <BigKpi
          label={t("visibility_change")}
          value={digest ? `${visChange > 0 ? "+" : ""}${visChange}%` : "—"}
          tone={visChange > 0 ? "up" : visChange < 0 ? "down" : "neutral"}
        />
        <BigKpi label={t("audit_health")} value={digest ? `${digest.audit_health}/100` : "—"} />
        <BigKpi label={t("critical_issues")} value={digest?.critical_issues ?? "—"} tone="down" />
      </div>

      {/* Movers */}
      <div data-testid={REP.digestCard} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MoverBlock title={t("top_gainers")} icon={TrendingUp} tone="up" movers={digest?.top_movers_up ?? []} />
        <MoverBlock title={t("top_losers")} icon={TrendingDown} tone="down" movers={digest?.top_movers_down ?? []} />
      </div>

      {/* Automated subscription */}
      <div
        data-testid={REP.subscriptionCard}
        className="border border-border bg-card rounded-lg p-6"
      >
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-accent-action" />
              <h2 className="font-display text-lg font-medium tracking-tight">
                {t("weekly_subscription")}
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {t("subscription_description")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
              {subEnabled ? t("enabled") : t("disabled")}
            </span>
            <Switch
              data-testid={REP.subEnabledToggle}
              checked={subEnabled}
              onCheckedChange={setSubEnabled}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <Label>Email</Label>
            <Input
              data-testid={REP.subEmailInput}
              type="email"
              placeholder={t("email_placeholder")}
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              className="h-10 font-mono text-sm"
            />
          </div>
          <div>
            <Label>{t("weekday")}</Label>
            <Select value={subWeekday} onValueChange={setSubWeekday}>
              <SelectTrigger data-testid={REP.subWeekdaySelect} className="h-10 font-mono text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map((d) => (
                  <SelectItem key={d} value={d} className="font-mono text-sm">
                    {t(d)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("hour_utc")}</Label>
            <Select value={subHour} onValueChange={setSubHour}>
              <SelectTrigger data-testid={REP.subHourSelect} className="h-10 font-mono text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }).map((_, i) => (
                  <SelectItem key={i} value={String(i)} className="font-mono text-sm">
                    {String(i).padStart(2, "0")}:00
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border flex-wrap gap-3">
          <div className="text-xs font-mono text-muted-foreground">
            {t("last_sent")}:{" "}
            <span className="text-foreground">
              {subscription?.last_sent_at
                ? new Date(subscription.last_sent_at).toLocaleString()
                : t("never_sent")}
            </span>
            {!emailEnabled && (
              <div className="mt-1 text-destructive">{t("email_configure_hint")}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid={REP.subRunNowBtn}
              variant="outline"
              onClick={() => runNowMutation.mutate()}
              disabled={!subscription?.subscription_id || !subEnabled || runNowMutation.isPending}
              className="h-10 gap-2"
            >
              <Send className="h-4 w-4" />
              {t("send_now")}
            </Button>
            <Button
              data-testid={REP.subSaveBtn}
              onClick={() => saveSubMutation.mutate()}
              disabled={!subEmail || saveSubMutation.isPending}
              className="h-10 gap-2"
            >
              {saveSubMutation.isPending ? "…" : t("save_and_activate")}
            </Button>
          </div>
        </div>
      </div>

      {/* Manual email sender (one-off) */}
      <div className="border border-border bg-card rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-lg font-medium tracking-tight">
              {t("send_by_email")}
            </h2>
            {!emailEnabled && (
              <p className="text-xs text-muted-foreground mt-1">{t("email_configure_hint")}</p>
            )}
          </div>
          <div
            className={cn(
              "text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded",
              emailEnabled
                ? "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))]"
                : "bg-secondary text-muted-foreground",
            )}
          >
            resend · {emailEnabled ? t("integration_configured") : t("integration_missing")}
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid={REP.emailInput}
            type="email"
            placeholder={t("email_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 font-mono text-sm max-w-md"
          />
          <Button
            data-testid={REP.sendEmailBtn}
            onClick={() => email && emailMutation.mutate()}
            disabled={!email || emailMutation.isPending}
            className="h-11 gap-2"
          >
            <Mail className="h-4 w-4" />
            {emailMutation.isPending ? "…" : t("send_by_email")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mb-1.5">
      {children}
    </div>
  );
}

function BigKpi({ label, value, delta, tone }) {
  const cls =
    tone === "up" || (typeof delta === "number" && delta > 0)
      ? "text-[hsl(var(--chart-3))]"
      : tone === "down" || (typeof delta === "number" && delta < 0)
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="border border-border bg-card rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
        {label}
      </div>
      <div className={cn("font-mono text-3xl sm:text-4xl tabular tracking-tight mt-4", cls)}>
        {value}
      </div>
      {typeof delta === "number" && (
        <div
          className={cn(
            "text-xs font-mono tabular mt-2",
            delta > 0 ? "text-[hsl(var(--chart-3))]" : delta < 0 ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {Math.abs(delta)}%
        </div>
      )}
    </div>
  );
}

function MoverBlock({ title, icon: Icon, tone, movers }) {
  const toneColor = tone === "up" ? "text-[hsl(var(--chart-3))]" : "text-destructive";
  return (
    <div className="border border-border bg-card rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={cn("h-4 w-4", toneColor)} />
        <h2 className="font-display text-lg font-medium tracking-tight">{title}</h2>
      </div>
      {movers.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">—</div>
      ) : (
        <ul className="space-y-2">
          {movers.map((m, i) => (
            <li
              key={i}
              className="flex items-center justify-between py-2 border-b border-border/70 last:border-0"
            >
              <span className="text-sm truncate mr-4">{m.keyword}</span>
              <div className="flex items-center gap-4 shrink-0">
                <span className="font-mono text-xs tabular text-muted-foreground">#{m.position}</span>
                <span className={cn("font-mono text-xs tabular w-14 text-right", toneColor)}>
                  {tone === "up" ? "▲" : "▼"} {Math.abs(m.delta)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
